import { ReactNode } from 'react';
import clsx from 'clsx';
import { HugeiconsIcon, IconSvgElement } from '@hugeicons/react';
import { ClickeableColor } from '../types/colors';

export interface NButtonProps {
  children?: ReactNode;
  onClick?: () => void;
  size?: 'xs' | 'small' | 'medium' | 'large';
  color?: ClickeableColor;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  hugeIcon?: IconSvgElement;
  type?: 'button' | 'submit' | 'reset';
}

export const NButton = ({
                          children,
                          onClick,
                          size = 'medium',
                          color = 'primary',
                          disabled = false,
                          loading = false,
                          className,
                          hugeIcon,
                          type = 'button'
                        }: NButtonProps) => {
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs rounded-md',
    small: 'px-4 py-2 text-sm rounded-lg',
    medium: 'px-6 py-3 text-sm rounded-xl',
    large: 'px-7 py-4 text-base rounded-2xl'
  }[size];

  const colorClasses = {
    primary: 'bg-primary text-black hover:bg-primary/80',
    secondary: 'bg-neutral-900 text-white hover:bg-neutral-800',
    tertiary: 'bg-slate-200 text-slate-900 hover:bg-slate-300',
    outline: 'text-slate-900 hover:bg-slate-100 ring ring-slate-300',
    transparent: 'bg-transparent text-slate-900 hover:bg-slate-100',
    link: 'text-info-600 hover:text-info-700 hover:bg-info-50',
    danger: 'bg-error-600 text-white hover:bg-error-700',
    dangerOutline: 'text-error-600 hover:bg-error-100 hover:text-error-700 ring ring-error-300',
    dangerTransparent: 'text-error-600 hover:bg-error-100 hover:text-error-700'
  }[color];

  const loadingClasses = {
    primary: 'bg-primary-loading',
    secondary: 'bg-secondary-loading',
    tertiary: 'bg-tertiary-loading',
    outline: 'bg-tertiary-loading',
    transparent: 'bg-tertiary-loading',
    link: 'bg-info-loading',
    danger: 'bg-error-loading',
    dangerOutline: 'bg-error-loading',
    dangerTransparent: 'bg-error-loading'
  }[color];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex cursor-pointer box-border relative items-center justify-center font-medium transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none select-none text-nowrap shrink-0 self-start grow-0 line-clamp-1',
        sizeClasses,
        colorClasses,
        className
      )}
    >
      {hugeIcon &&
        <HugeiconsIcon size={16} strokeWidth={2} className={'relative -left-1.5'} icon={hugeIcon} />
      }
      {children}
      {loading && (
        <div className={clsx('absolute w-full h-full top-0', loadingClasses)} />
      )}
    </button>
  );
};
